/**
 * Écran de connexion.
 *
 * Trois temps possibles, comme le décide l'API — le formulaire ne devine
 * rien : identifiants, puis soit un code de double authentification si un
 * secret est déjà enrôlé, soit un parcours d'enrôlement complet (Google
 * Authenticator) si le rôle l'impose et qu'aucun secret n'existe encore.
 */
import { useState, type FormEvent } from 'react';

import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { ChampMotDePasse } from '../composants/ChampMotDePasse';

type Etape =
  | { readonly nom: 'identifiants' }
  | { readonly nom: 'code'; readonly challenge: string }
  | {
      readonly nom: 'configuration-mfa';
      readonly challenge: string;
      readonly qrDataUri: string | null;
      readonly secretSaisieManuelle: string;
    };

export function LoginPage() {
  const { login, verifyMfa, confirmMfaSetup } = useAuth();

  const [etape, setEtape] = useState<Etape>({ nom: 'identifiants' });
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [code, setCode] = useState('');
  const [seSouvenirAppareil, setSeSouvenirAppareil] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const soumettreIdentifiants = async (event: FormEvent) => {
    event.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const resultat = await login(email.trim(), motDePasse);
      if (resultat.status === 'MFA_REQUIRED') {
        setEtape({ nom: 'code', challenge: resultat.mfaChallenge });
      } else if (resultat.status === 'MFA_SETUP_REQUIRED') {
        setEtape({
          nom: 'configuration-mfa',
          challenge: resultat.mfaChallenge,
          qrDataUri: resultat.qrDataUri,
          secretSaisieManuelle: resultat.secretSaisieManuelle,
        });
      }
      // Si le serveur répond AUTHENTICATED, le contexte a déjà basculé et cet
      // écran disparaît : rien à faire ici.
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Connexion impossible. Réessayez.');
    } finally {
      setEnCours(false);
    }
  };

  const soumettreCode = async (event: FormEvent) => {
    event.preventDefault();
    if (etape.nom !== 'code') return;
    setErreur(null);
    setEnCours(true);
    try {
      await verifyMfa(etape.challenge, code.trim(), email.trim(), seSouvenirAppareil);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Code refusé. Réessayez.');
      setCode('');
    } finally {
      setEnCours(false);
    }
  };

  const soumettreConfigurationMfa = async (event: FormEvent) => {
    event.preventDefault();
    if (etape.nom !== 'configuration-mfa') return;
    setErreur(null);
    setEnCours(true);
    try {
      await confirmMfaSetup(etape.challenge, code.trim(), email.trim(), seSouvenirAppareil);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Code refusé. Réessayez.');
      setCode('');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="ecran-connexion">
      <div className="carte-connexion">
        <div className="marque">
          <strong>Air Indu SaaS</strong>
          <span>Maintenance, interventions et gestion technique</span>
        </div>

        {erreur && <div className="message erreur">{erreur}</div>}

        {etape.nom === 'identifiants' && (
          <form onSubmit={soumettreIdentifiants}>
            <div className="champ">
              <label htmlFor="email">Adresse électronique</label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="champ">
              <label htmlFor="mdp">Mot de passe</label>
              <ChampMotDePasse id="mdp" autoComplete="current-password" required value={motDePasse} onChange={setMotDePasse} />
            </div>

            <button type="submit" className="pleine-largeur" disabled={enCours}>
              {enCours ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>
        )}

        {etape.nom === 'code' && (
          <form onSubmit={soumettreCode}>
            <div className="message info">
              Saisissez le code à 6 chiffres de votre application d’authentification.
            </div>

            <div className="champ">
              <label htmlFor="code">Code de vérification</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                autoFocus
                maxLength={32}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <p className="aide">
                Un code de récupération à usage unique est également accepté.
              </p>
            </div>

            <label className="case" style={{ margin: '10px 0' }}>
              <input
                type="checkbox"
                checked={seSouvenirAppareil}
                onChange={(e) => setSeSouvenirAppareil(e.target.checked)}
              />
              Se souvenir de cet appareil pendant 30 jours
            </label>

            <button type="submit" className="pleine-largeur" disabled={enCours}>
              {enCours ? 'Vérification…' : 'Valider'}
            </button>

            <button
              type="button"
              className="secondaire pleine-largeur"
              style={{ marginTop: 8 }}
              onClick={() => {
                setEtape({ nom: 'identifiants' });
                setCode('');
                setErreur(null);
              }}
            >
              Revenir
            </button>
          </form>
        )}

        {etape.nom === 'configuration-mfa' && (
          <form onSubmit={soumettreConfigurationMfa}>
            <div className="message info">
              Cet espace exige la double authentification. Installez
              l’application <strong>Google Authenticator</strong> (ou toute
              application compatible TOTP) sur votre téléphone, puis scannez
              ce code :
            </div>

            {etape.qrDataUri && (
              <div className="qr-mfa" style={{ textAlign: 'center', margin: '16px 0' }}>
                <img src={etape.qrDataUri} alt="QR code d’enrôlement MFA" width={220} height={220} />
              </div>
            )}

            <p className="aide">
              QR illisible ? Saisissez cette clé à la main dans l’application :{' '}
              <code>{etape.secretSaisieManuelle}</code>
            </p>

            <div className="champ">
              <label htmlFor="code-config">Code affiché par l’application</label>
              <input
                id="code-config"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                autoFocus
                maxLength={32}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>

            <label className="case" style={{ margin: '10px 0' }}>
              <input
                type="checkbox"
                checked={seSouvenirAppareil}
                onChange={(e) => setSeSouvenirAppareil(e.target.checked)}
              />
              Se souvenir de cet appareil pendant 30 jours
            </label>

            <button type="submit" className="pleine-largeur" disabled={enCours}>
              {enCours ? 'Validation…' : 'Activer la double authentification'}
            </button>

            <button
              type="button"
              className="secondaire pleine-largeur"
              style={{ marginTop: 8 }}
              onClick={() => {
                setEtape({ nom: 'identifiants' });
                setCode('');
                setErreur(null);
              }}
            >
              Revenir
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
